import { IconLabel } from "@core/components/IconLabel";
import { usePromptContext } from "@core/domains/chat/prompt/PromptContext";
import { preprocessCiteTags } from "@core/domains/threads/utils/citationUtils";
import { getScreenType } from "@core/lib/ui/getScreenType";
import { StoryTileDrawerScreen } from "@core/screens/drawer/StoryTileDrawerScreen";
import { useUIState } from "@core/ui/UIState";
import { useWire } from "@core/wire";
import { stAnalytics } from "@repo/analytics";
import type { curator } from "@repo/client";
import { Motion } from "@repo/solid-motionone";
import { debounce } from "@solid-primitives/scheduled";
import { TbWorldOff, TbWorldPlus } from "solid-icons/tb";
import { type Component, For, Match, Show, Switch, createEffect, createSignal, onCleanup, onMount } from "solid-js";
import type { DOMElement } from "solid-js/jsx-runtime";
import { twMerge } from "tailwind-merge";
import { TextSelectionMenu } from "../TextSelectionMenu";
import "./codeHighlightTheme.css";
import { MarkdownRenderer } from "./MarkdownRenderer";
import { TextUnitStoryTiles } from "./TextUnitStoryTiles";
import { TextUnitV1ActionsBar } from "./TextUnitV1ActionsBar";
import { TextUnitV1TextSelection } from "./TextUnitV1TextSelection";

// Global promise cache to prevent concurrent API calls for the same thread
const threadLoadingPromises = new Map<string, Promise<any>>();

export type TextInstructionUnitV1Props = {
  message: curator.MessageTextV1;
  class?: string;
  disableActions?: boolean;
  isThreadReadOnly: boolean;
  includeURL?: {
    text: string;
    url: string;
  };
} & {
  promptMessageId: string;
};

export const TextUnitV1 = (props: TextInstructionUnitV1Props) => {
  const citationRefsCache: Record<string, curator.Citation | undefined> = {};
  const { editor, typePrompt } = usePromptContext();
  const wire = useWire();
  const ui = useUIState();
  const [showCitations] = ui.showCitations;
  const [, setRightContents] = ui.rightDrawerContents;
  const [rightOpen, setRightOpen] = ui.rightDrawer;
  const [ref, setRef] = createSignal<HTMLElement>();

  const [tiles, setTiles] = createSignal<curator.Citation[]>([]);
  const [assetReferencesMap, setAssetReferencesMap] = createSignal<curator.AssetRefMap | undefined>();

  const [citationDisplayData, setCitationDisplayData] = createSignal<{
    map: Map<string, number>; // assetId (chunkId) -> displayNumber (all citations)
    validCitationsMap: Map<string, number>; // assetId (chunkId) -> displayNumber (only valid citations)
    orderedTilesForDisplay: Array<curator.Citation & { displayNumber: number }>;
  }>({ map: new Map(), validCitationsMap: new Map(), orderedTilesForDisplay: [] });

  const trigger = async () => {
    const refs = props.message.provenance.scoped?.citations;
    const threadId = props.message.threadId;

    // Early return if we already have tiles for the current refs
    const currentTiles = tiles();
    if (refs?.length && currentTiles.length >= refs.length) {
      const hasAllRefsInTiles = refs.every((ref) =>
        currentTiles.some((tile) => citationRefsCache[ref] && citationRefsCache[ref]?.citationId === tile.citationId),
      );
      if (hasAllRefsInTiles) {
        return;
      }
    }

    // Make sure to populate the cache with the citations from the current thread in case they're already loaded
    const snapshotCitations = wire.services.threads.snapshot.context.knowledgeProvenance.citations;
    if (snapshotCitations) {
      Object.entries(snapshotCitations).forEach(([backendRefId, citationObj]) => {
        citationRefsCache[backendRefId] = citationObj;
      });
    }

    // If any of the citation refs are not in the cache, then fetch the thread to get the refs
    // or if the message is done and we still don't have a proper assetReferencesMap from the snapshot
    let shouldFetch = refs?.some((r) => !citationRefsCache[r]);
    if (!shouldFetch && props.message.isDone && !wire.services.threads.snapshot.context.knowledgeProvenance?.assetRef) {
      shouldFetch = true;
    }

    if (!shouldFetch && refs?.length && props.message.isDone) {
      const snapshotAssetRef = wire.services.threads.snapshot.context.knowledgeProvenance?.assetRef;
      const hasAllAssetRefs = refs.every((r) => {
        const citation = citationRefsCache[r];
        return !citation?.assetReferenceId || snapshotAssetRef?.[citation.assetReferenceId];
      });
      if (!hasAllAssetRefs) {
        shouldFetch = true;
      }
    }

    if (shouldFetch) {
      // Check if there's already a promise for this thread
      let loadPromise = threadLoadingPromises.get(threadId);

      if (!loadPromise) {
        // Create new promise for this thread
        loadPromise = wire.services.threads.loadThread(props.message.threadId).finally(() => {
          // Clean up the promise from cache after a delay to account for retries
          setTimeout(() => {
            threadLoadingPromises.delete(threadId);
          }, 5000);
        });
        threadLoadingPromises.set(threadId, loadPromise);
      }

      try {
        const result = await loadPromise;

        const tilesData = (refs || []) // Handle refs being undefined if shouldFetch was true due to other reasons
          .map((r) => {
            const found = Object.keys(result.data.knowledgeProvenance?.citations || {}).find((c) => c === r);
            if (!found) return undefined; // Ensure to return undefined, not void
            return result.data.knowledgeProvenance?.citations[found];
          })
          .filter((r): r is curator.Citation => r !== undefined);

        (refs || []).forEach((r) => {
          // Handle refs being undefined
          if (result.data.knowledgeProvenance?.citations?.[r]) {
            // Check if citation exists before assigning
            citationRefsCache[r] = result.data.knowledgeProvenance.citations[r];
          }
        });
        setTiles(tilesData);
        setAssetReferencesMap(result.data.knowledgeProvenance?.assetRef);
        // ...
        return;
      } catch (error) {
        // Don't update state on error, let component retry later
        return;
      }
    }
    // If we are here, shouldFetch is false. This is the "no fetch" path.

    if (refs?.length) {
      // If there are refs, they must be in cache, or shouldFetch would have been true.
      setTiles(refs.map((r) => (r ? citationRefsCache[r] : null)).filter((r): r is NonNullable<typeof r> => r != null));

      const snapshotAssetRef = wire.services.threads.snapshot.context.knowledgeProvenance?.assetRef;
      if (snapshotAssetRef && Object.keys(snapshotAssetRef).length > 0) {
        setAssetReferencesMap(snapshotAssetRef);
      } else {
        // If assetReferencesMap is currently undefined, and snapshot is also undefined for assetRef, set it to undefined.
        // If assetReferencesMap already has a value, preserve it.
        if (!assetReferencesMap()) {
          setAssetReferencesMap(undefined);
        }
      }
    } else {
      // No refs (and not fetching)
      setTiles([]);
      setAssetReferencesMap(undefined);
    }
  };

  const handleCiteClick = (dataFromLink: {
    chunkId: string;
    searchIdx: string;
    sourceName: string;
    displayNumber: number; // The displayed number, e.g., 4 for "[4]"
    messageId: string; // The ID of the message from which the click originated
  }) => {
    // First, ensure this click is relevant to the current message context.
    // props.message.messageId is the ID of the message this TextUnitV1 instance is rendering.
    if (dataFromLink.messageId !== props.message.messageId) {
      return; // Or handle as an error/unexpected situation
    }

    // Use citationDisplayData().orderedTilesForDisplay, which is already scoped to the current message
    // and contains the displayNumber.
    const tilesForCurrentMessage = citationDisplayData().orderedTilesForDisplay;

    const storyTileDataObject = tilesForCurrentMessage.find(
      (tile) => tile.chunkId === dataFromLink.chunkId && tile.displayNumber === dataFromLink.displayNumber,
    );

    if (storyTileDataObject) {
      setRightContents(() => () => (
        <StoryTileDrawerScreen
          tiles={tilesForCurrentMessage}
          initial={storyTileDataObject.citationId}
          assetRefMap={assetReferencesMap()}
        />
      ));
      setRightOpen(true);
    }
  };

  // Track previous isDone state to only trigger when streaming completes
  const [lastIsDone, setLastIsDone] = createSignal<boolean | undefined>(undefined);

  createEffect(() => {
    const refs = props.message.provenance.scoped?.citations;
    const isDone = props.message.isDone;

    // If we have citations on first render, trigger immediately
    if (lastIsDone() === undefined) {
      setLastIsDone(isDone);
      if (refs?.length) {
        trigger();
      }
      return;
    }

    // Only trigger when message transitions from not done to done
    if (!lastIsDone() && isDone) {
      setLastIsDone(isDone);
      trigger();

      // Reduced timeout workaround for race condition - single retry after reasonable delay
      setTimeout(() => {
        const currentRefs = props.message.provenance.scoped?.citations;
        if (currentRefs?.length) {
          trigger();
        }
      }, 3000);
      return;
    }

    // Update tracking variable
    setLastIsDone(isDone);

    // Handle case where citations are added while message is already done
    if (isDone && refs?.length && tiles().length === 0) {
      trigger();
    }
  });

  createEffect(() => {
    const markdownText = props.message.parts.join("\\n");
    const currentTilesFromSignal = tiles();

    if (!markdownText) {
      setCitationDisplayData({ map: new Map(), validCitationsMap: new Map(), orderedTilesForDisplay: [] });
      return;
    }

    // Apply preprocessing to handle malformed cite tags
    const processedMarkdown = preprocessCiteTags(markdownText);

    const newAssetIdToDisplayNumberMap = new Map<string, number>();
    let uniqueCiteCounter = 0;
    // This regex should match the one in MarkdownRenderer: /<cite>\s*([^<]*?)\s*<\/cite>/g
    const citeRegex = /<cite>\s*([^<]*?)\s*<\/cite>/g;
    let match: RegExpExecArray | null = citeRegex.exec(processedMarkdown);

    while (match !== null) {
      const assetId = match[1]?.trim(); // This is the chunkId
      if (assetId && !newAssetIdToDisplayNumberMap.has(assetId)) {
        uniqueCiteCounter++;
        newAssetIdToDisplayNumberMap.set(assetId, uniqueCiteCounter);
      }
      match = citeRegex.exec(processedMarkdown);
    }

    const relevantTilesForDisplay: Array<curator.Citation & { displayNumber: number }> = [];
    const validCitationsMap = new Map<string, number>();

    currentTilesFromSignal.forEach((tile) => {
      // Assuming tile.chunkId is the asset_id used in <cite> tags and for the map keys
      const assetId = tile.chunkId;
      if (assetId && newAssetIdToDisplayNumberMap.has(assetId)) {
        const displayNumber = newAssetIdToDisplayNumberMap.get(assetId)!;
        // Filter by relevance, similar to TextUnitStoryTiles's original internal filter
        if (tile.relevance >= 0) {
          relevantTilesForDisplay.push({ ...tile, displayNumber });
          validCitationsMap.set(assetId, displayNumber);
        }
      }
    });

    // Sort them by the display number
    relevantTilesForDisplay.sort((a, b) => a.displayNumber - b.displayNumber);

    setCitationDisplayData({
      map: newAssetIdToDisplayNumberMap,
      validCitationsMap,
      orderedTilesForDisplay: relevantTilesForDisplay,
    });
  });

  return (
    <Motion.div
      // {...getUnitAnimationConfig()}
      class="dark:text-slate-200 text-gray-800 text-sm md:text-base scroll-mt-32"
      data-block={props.message.messageId}
    >
      <div ref={setRef} class={twMerge("flex flex-col break-words min-h-[32px]", props.class)}>
        <TextUnitV1Markdown
          isThreadReadOnly={props.isThreadReadOnly}
          message={props.message}
          includeURL={props.includeURL}
          onCiteClick={handleCiteClick}
          citationNumberMap={citationDisplayData().validCitationsMap}
          showCitations={showCitations()}
        />
      </div>

      <TextUnitV1TextSelection isThreadReadOnly={props.isThreadReadOnly} ref={ref} />
      <Switch>
        <Match
          when={
            props.message?.provenance?.scoped?.worldKnowledge &&
            !props.message.provenance.scoped.collectionReferenceIDs?.length &&
            !props.message.provenance.scoped.collectionAssets?.length &&
            !props.message.provenance.scoped.explicitAssets?.length
          }
        >
          <IconLabel
            class="select-none"
            icon={TbWorldPlus}
            label="Includes just public knowledge."
            modifiers={["italic", "block"]}
          />
        </Match>
        <Match when={props.message?.provenance?.scoped?.worldKnowledge}>
          <IconLabel
            class="select-none"
            icon={TbWorldPlus}
            label="Includes public knowledge."
            modifiers={["italic", "block"]}
          />
        </Match>
        <Match when={!props.message?.provenance?.scoped?.worldKnowledge}>
          <IconLabel
            class="select-none"
            icon={TbWorldOff}
            label="Excludes public knowledge."
            modifiers={["italic", "block"]}
          />
        </Match>
      </Switch>
      <Show when={!props.disableActions}>
        <Show when={showCitations() && citationDisplayData().orderedTilesForDisplay.length > 0}>
          <TextUnitStoryTiles
            tiles={citationDisplayData().orderedTilesForDisplay}
            assetReferences={assetReferencesMap()}
          />
        </Show>

        <TextUnitV1ActionsBar
          promptMessageId={props.promptMessageId}
          message={props.message}
          ref={ref}
          isThreadReadOnly={props.isThreadReadOnly}
        />

        <Show when={props.message.textSuggestions}>
          <div>
            <span class="text-[0.625rem] text-violet-500 dark:text-violet-400 underline underline-offset-4 uppercase tracking-wider">
              Prompt Suggestions:
            </span>
            <ul class="block list-disc ml-4 pt-2">
              <For each={props.message.textSuggestions}>
                {(suggestion) => (
                  <li class="list-item list-disc list-outside dark:text-purple-400 text-black pl-2 text-left">
                    <button
                      class="inline-block w-auto text-left hover:underline underline-offset-2 leading-normal text-base min-h-max"
                      type={"button"}
                      onClick={() => {
                        editor()?.commands.focus();
                        typePrompt(suggestion, { highlight: true });
                      }}
                    >
                      {suggestion}
                    </button>
                  </li>
                )}
              </For>
            </ul>
          </div>
        </Show>
      </Show>
    </Motion.div>
  );
};

const TextUnitV1Markdown: Component<{
  message: curator.MessageTextV1;
  isThreadReadOnly: boolean;
  includeURL?: {
    text: string;
    url: string;
  };
  onCiteClick?: (data: {
    chunkId: string;
    searchIdx: string;
    sourceName: string;
    displayNumber: number;
    messageId: string;
  }) => void;
  citationNumberMap: Map<string, number>; // Added from parent
  showCitations: boolean; // Added this
}> = (props) => {
  const [el, setEl] = createSignal<HTMLElement>();
  const [domRect, setDomRect] = createSignal<DOMRect>();
  const [range, setRange] = createSignal<Range>();

  // Use prevent hide to keep the dropdown alive when a user
  // exits all markdown elements but is hovering the mouse on the dropdown
  let preventHide = false;
  // Used to block the dropdown from showing when the text selction dropdown is out
  let preventShow = false;

  // Debouncing it to prevent flickering when switching in between elements fast
  const setRectDebounced = debounce((value: DOMRect | undefined) => setDomRect(value), 500);

  // The hover text modal is different from
  onMount(() => {
    const listener = () => {
      const selection = getSelection();
      if (selection?.rangeCount && selection?.rangeCount > 0) {
        setRange(selection?.getRangeAt(0));
      }
      if ((selection?.toString() || "") !== "") {
        setRectDebounced.clear();
        setDomRect();
        preventShow = true;
      } else {
        preventShow = false;
      }
    };
    const focusOut = (event: FocusEvent) => {
      const _range = range();
      if (_range && !_range?.collapsed) {
        if (event.relatedTarget instanceof HTMLElement && event.relatedTarget.id.includes("dropdownmenu")) {
          getSelection()?.removeAllRanges();
          getSelection()?.addRange(_range);
        }
      }
    };
    document.addEventListener("selectionchange", listener);
    document.addEventListener("focusout", focusOut);
    onCleanup(() => {
      document.removeEventListener("selectionchange", listener);
      document.removeEventListener("focusout", focusOut);
    });
  });

  const onHover = (
    e: MouseEvent & {
      target: DOMElement;
    },
  ) => {
    if (preventShow) return;
    if (!(e.target instanceof HTMLElement)) return;
    setEl(e.target);
    setRectDebounced.clear();
    const elRect = e.target.getBoundingClientRect();

    const messageEl = document.querySelector(`[data-block="${props.message.messageId}"]`);
    const messageRect = messageEl?.getBoundingClientRect() ?? elRect;

    setRectDebounced({
      top: elRect.top,
      height: elRect.height,
      bottom: elRect.bottom,
      y: elRect.y,
      left: messageRect.left,
      right: messageRect.right,
      width: messageRect.width,
      x: messageRect.x,
      toJSON: elRect.toJSON,
    });
  };

  const onUnhover = () => {
    if (preventHide) return;
    setRectDebounced(undefined);
  };

  return (
    <>
      <TextSelectionMenu
        isThreadReadOnly={props.isThreadReadOnly}
        rect={domRect()}
        from="block"
        animatedMovement
        onClose={() => {
          preventHide = false;
          setRectDebounced.clear();
          setDomRect();
        }}
        opts={{
          preventScroll: false,
          placement: "right-start",
          hideWhenDetached: true,
        }}
        content={{
          onMouseEnter: () => {
            preventHide = true;
            setRectDebounced.clear();
          },
          onMouseLeave: (e) => {
            if ("toElement" in e && e.toElement instanceof HTMLElement) {
              const id = e.toElement.id;
              if (!id.includes("dropdownmenu")) {
                preventShow = true;
                preventHide = false;
                onUnhover();
                setTimeout(() => {
                  preventShow = false;
                }, 510);
                return;
              }
            }

            preventHide = false;
            onUnhover();
          },
        }}
        getText={() => el()?.innerText || ""}
        getEl={el}
      />

      <MarkdownRenderer
        includeURL={props.includeURL}
        prefix={props.message.messageId}
        md={props.message.parts.join("\n")}
        onMouseOverChild={onHover}
        onMouseOutChild={onUnhover}
        onCiteClick={props.onCiteClick}
        showCitations={props.showCitations}
        citationNumberMap={props.citationNumberMap}
      />
    </>
  );
};
